








































































































































import { Component } from "vue-property-decorator";
import MiraBarRowComponent from "@/components/mira/MiraBarRow.vue";
import MiraDetailCardComponent from "@/components/mira/MiraDetailCard.vue";
import MiraFactsComponent from "@/components/mira/MiraFacts.vue";
import MiraKpiTileComponent from "@/components/mira/MiraKpiTile.vue";
import MiraKpiTileGridComponent from "@/components/mira/MiraKpiTileGrid.vue";
import MiraComparisonCardComponent from "@/components/mira/MiraComparisonCard.vue";
import MiraScreenBase from "@/components/mira/MiraScreenBase";
import MiraScreenHeaderComponent from "@/components/mira/MiraScreenHeader.vue";
import MiraStackedBarComponent from "@/components/mira/MiraStackedBar.vue";
import MiraTrendCardComponent from "@/components/mira/MiraTrendCard.vue";
import MiraStackedTrendCardComponent from "@/components/mira/MiraStackedTrendCard.vue";
import MiraUnlockListComponent from "@/components/mira/MiraUnlockList.vue";
import { MiraSection } from "@/data/models/Mira";
import { MiraSectionUnlocks, collectUnlocks } from "@/utils/MiraDataQuality";
import { MiraBar, MiraSegment, MiraTile } from "@/utils/MiraDataQualityView";
import { MiraFact } from "@/utils/MiraDryingView";
import {
  answerFacts,
  approvalFacts,
  buildAnswerSegments,
  buildApprovalBars,
  buildInsuranceTiles,
  buildInsuranceCaseSegments,
  buildInsurerRows,
  insuredProjectsText,
  insuranceInPreparation,
  insuranceTrendFormatter,
} from "@/utils/MiraInsuranceView";

/**
 * Mira Versicherungen: what happens to the offers a company sends the insurer from IRES Project, how
 * long the insurer takes to answer, how often it accepts, and how the drying compares with the days it
 * approved.
 *
 * Built like the other Mira screens (`MiraScreenBase`). The HyDry / conventional split of the approved
 * drying is never shown, and INS-007 is never named as in preparation (Taimoor, 2026-10-01); both
 * enforced in `@/utils/MiraInsuranceView`. See `docs/analytics/mira-office-screens.md`.
 */
@Component({
  components: {
    "mira-bar-row": MiraBarRowComponent,
    "mira-detail-card": MiraDetailCardComponent,
    "mira-facts": MiraFactsComponent,
    "mira-kpi-tile": MiraKpiTileComponent,
    "mira-kpi-tile-grid": MiraKpiTileGridComponent,
    "mira-screen-header": MiraScreenHeaderComponent,
    "mira-stacked-bar": MiraStackedBarComponent,
    "mira-comparison-card": MiraComparisonCardComponent,
    "mira-trend-card": MiraTrendCardComponent,
    "mira-stacked-trend-card": MiraStackedTrendCardComponent,
    "mira-unlock-list": MiraUnlockListComponent,
  },
})
export default class MiraInsuranceComponent extends MiraScreenBase {
  get section(): MiraSection {
    return MiraSection.INSURANCE;
  }

  get titleKey(): string {
    return "mira.insurance.title";
  }

  get tiles(): MiraTile[] {
    return buildInsuranceTiles(this.period, this.comparison, this.viewContext);
  }

  /**
   * The insurer mix (MIRA-INS-001): the Versicherer card's bars. Present for a GZ-only client whose only
   * insurance figure is this one, so the Versicherungen screen then shows this card alone.
   */
  get insurerRows(): MiraBar[] {
    return buildInsurerRows(this.period, this.viewContext);
  }

  /** The card's first bar: Mit Versicherung / Keine Angabe / Direktauftrag over every finished project (#2610137). */
  get insuranceCaseSegments(): MiraSegment[] {
    return buildInsuranceCaseSegments(this.period, this.viewContext);
  }

  /** The heading above the insurer bars: the projects with an insurer named they are shares of (#2610137). */
  get insuredProjectsHeading(): string {
    return insuredProjectsText(this.period, this.viewContext) ?? "";
  }

  get answerSegments(): MiraSegment[] {
    return buildAnswerSegments(this.period, this.viewContext);
  }

  get answerFacts(): MiraFact[] {
    return answerFacts(this.period, this.viewContext);
  }

  get approvalBars(): MiraBar[] {
    return buildApprovalBars(this.period, this.viewContext);
  }

  get approvalFacts(): MiraFact[] {
    return approvalFacts(this.period, this.viewContext);
  }

  get inPreparation(): string[] {
    return insuranceInPreparation(this.period, this.viewContext);
  }

  /**
   * Only this section's figures waiting on data: the other sections have their own screens. Computed
   * here rather than through `super`, which vue-class-component's computed getters do not support.
   */
  get unlocks(): MiraSectionUnlocks[] {
    return collectUnlocks(this.sections, this.selectedPeriod).filter((group) => group.section === MiraSection.INSURANCE);
  }

  trendFormatter(metricId: string): (value: number) => string {
    return insuranceTrendFormatter(metricId, this.viewContext, this.period);
  }
}
